import React from 'react';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';
import {
  Experience,
  PersonalState,
  Skill,
  Work,
  Language,
  Course,
  LinkPdf,
  Hobby,
  Internship,
  Education,
} from '../interfaces/store';
import { WorkPdf } from './WorkPdf';
import { PersonaliaPdf } from './PersonalPdf';
import { SkillsPdf } from './SkillsPdf';
import { LanguagesPdf } from './LanguagePdf';
import { CoursesPdf } from './CoursePdf';
import { LinksPdf } from './LinkPdf';
import { HobbyPdf } from './HobbyPdf';
import {
  CHARS_PER_PAGE,
  SIDEPANEL_MAX_ITEMS_FIRST_PAGE,
  SIDEPANEL_MAX_ITEMS_PAGE,
} from '../interfaces/const';
import { EducationPdf } from './EducationPdf';
import { InternshipPdf } from './InternshipPdf';

const styles = StyleSheet.create({
  page: {
    paddingBottom: 0,
    fontSize: 12,
  },
  row: {
    flexDirection: 'row',
    width: '100%',
    height: '100%',
  },
  sidePanel: {
    width: '30%',
    backgroundColor: 'whitesmoke',
    padding: '6',
    height: '100%',
  },
  mainContent: {
    width: '70%',
    padding: '10',
    backgroundColor: '#f9f9f9',
  },
  avatar: {
    width: '125',
    height: '125',
    borderRadius: '25',
    margin: 'auto',
  },
  avatarContainer: {
    padding: '6',
    height: '190',
  },
  headingBorderline: {
    textAlign: 'center',
    borderBottom: '1 solid whitesmoke',
    fontSize: '21',
    paddingTop: '4',
    paddingBottom: '4',
    fontWeight: '500',
  },
  headingLabel: {
    fontSize: '21',
    fontWeight: '500',
    paddingTop: '9',
    paddingBottom: '9',
    color: 'black'
  },
});

interface Props {
  personaData: PersonalState;
  primaryColor: string;
  secondaryColor: string;
  fontColor: string;
  experiences: Experience[];
  skills: Skill[];
  languages: Language[];
  courses: Course[];
  links: LinkPdf[];
  hobbies: Hobby[];
}

export const DefaultPdf: React.FC<Props> = ({
  personaData,
  primaryColor,
  secondaryColor,
  fontColor,
  experiences,
  skills,
  languages,
  courses,
  links,
  hobbies,
}) => {
  // Process main content sections
  const work: Work[] = [];
  const internship: Internship[] = [];
  const education: Education[] = [];
  
  let totalChars = personaData.description.length;
  let mainContentNumberOfPages = 1;

  // Process experiences and calculate main content pages
  for (const experience of experiences) {
    if (experience.type === 'work') {
      totalChars += experience.description.length;
      totalChars += experience.role?.length || 0;
      if (totalChars > CHARS_PER_PAGE) {
        mainContentNumberOfPages++;
        totalChars = 0;
      }
      work.push({ ...experience, page: mainContentNumberOfPages - 1 } as Work);
    }

    if (experience.type === 'internship') {
      totalChars += experience.description.length;
      totalChars += experience.education?.length || 0;
      if (totalChars > CHARS_PER_PAGE) {
        mainContentNumberOfPages++;
        totalChars = 0;
      }
      internship.push({ ...experience, page: mainContentNumberOfPages - 1 } as Internship);
    }

    if (experience.type === 'education') {
      totalChars += experience.description.length;
      totalChars += experience.education?.length || 0;
      if (totalChars > CHARS_PER_PAGE) {
        mainContentNumberOfPages++;
        totalChars = 0;
      }
      education.push({ ...experience, page: mainContentNumberOfPages - 1 } as Education);
    }
  }

  // Organize side panel sections
  const sidePanelSections = {
    skills: {
      items: skills,
      Component: SkillsPdf,
      heading: 'Skills',
    },
    languages: {
      items: languages,
      Component: LanguagesPdf,
      heading: 'Languages',
    },
    courses: {
      items: courses,
      Component: CoursesPdf,
      heading: 'Courses',
    },
    links: {
      items: links,
      Component: LinksPdf,
      heading: 'Links',
    },
    hobbies: {
      items: hobbies,
      Component: HobbyPdf,
      heading: 'Hobbies',
    },
  };

  // Distribute items across pages
  const distributeItemsAcrossPages = () => {
    const pages: Array<Array<{
      type: string;
      items: any[];
      Component: React.FC<any>;
      heading: string;
    }>> = [[]];
    
    let currentPage = 0;
    let itemsOnCurrentPage = 0;
    
    Object.entries(sidePanelSections).forEach(([type, section]) => {
      const { items, Component, heading } = section;
      let remainingItems = [...items];
      
      while (remainingItems.length > 0) {
        const maxItemsForPage = currentPage === 0 
          ? SIDEPANEL_MAX_ITEMS_FIRST_PAGE 
          : SIDEPANEL_MAX_ITEMS_PAGE;
        
        const availableSpace = maxItemsForPage - itemsOnCurrentPage;
        
        if (availableSpace <= 0) {
          currentPage++;
          itemsOnCurrentPage = 0;
          pages[currentPage] = [];
          continue;
        }
        
        const itemsToTake = Math.min(availableSpace, remainingItems.length);
        const pageItems = remainingItems.slice(0, itemsToTake);
        
        if (pageItems.length > 0) {
          pages[currentPage].push({
            type,
            items: pageItems,
            Component,
            heading,
          });
          
          itemsOnCurrentPage += itemsToTake;
          remainingItems = remainingItems.slice(itemsToTake);
        }
        
        if (remainingItems.length > 0 && itemsOnCurrentPage >= maxItemsForPage) {
          currentPage++;
          itemsOnCurrentPage = 0;
          pages[currentPage] = [];
        }
      }
    });
    
    return pages;
  };

  const sidePanelPages = distributeItemsAcrossPages();
  const totalPages = Math.max(mainContentNumberOfPages, sidePanelPages.length);

  if (fontColor) {
    styles.headingLabel.color = fontColor
  }
  return (
    <Document>
      {Array.from({ length: totalPages }, (_, pageIndex) => (
        <Page key={`page-${pageIndex}`} size="A4" style={styles.page}>
          <View style={styles.row}>
            <View style={{ ...styles.sidePanel, backgroundColor: primaryColor }}>
              {pageIndex === 0 && (
                <>
                  <View style={styles.avatarContainer}>
                    <Image src={personaData.avatar} style={styles.avatar} />
                    <Text style={styles.headingBorderline}>{personaData.name}</Text>
                  </View>
                  <Text style={styles.headingLabel}>Personalia</Text>
                  <PersonaliaPdf color={secondaryColor} personaData={personaData} />
                </>
              )}
              
              {sidePanelPages[pageIndex]?.map((section, sectionIndex) => {
                const isFirstAppearanceOfType = !sidePanelPages
                  .slice(0, pageIndex)
                  .some(page => page.some(s => s.type === section.type));

                return (
                  <View key={`${section.type}-${sectionIndex}`}>
                    {isFirstAppearanceOfType && (
                      <Text style={styles.headingLabel}>{section.heading}</Text>
                    )}
                    <section.Component 
                      {...{ 
                        [`${section.type}`]: section.items,
                        color: secondaryColor,
                        fontColor 
                      }} 
                    />
                  </View>
                );
              })}
            </View>

            <View style={styles.mainContent}>
              {pageIndex === 0 && <Text>{personaData.description}</Text>}
              
              {education.filter(e => e.page === pageIndex).length > 0 && (
                <>
                  {education.findIndex(e => e.page === pageIndex) === 
                   education.findIndex(e => e.page !== undefined) && (
                    <Text style={styles.headingLabel}>Education</Text>
                  )}
                  <EducationPdf educationList={education.filter(e => e.page === pageIndex)} fontColor={fontColor}/>
                </>
              )}

              {internship.filter(i => i.page === pageIndex).length > 0 && (
                <>
                  {internship.findIndex(i => i.page === pageIndex) === 
                   internship.findIndex(i => i.page !== undefined) && (
                    <Text style={styles.headingLabel}>Internship</Text>
                  )}
                  <InternshipPdf internships={internship.filter(i => i.page === pageIndex)} fontColor={fontColor}/>
                </>
              )}

              {work.filter(w => w.page === pageIndex).length > 0 && (
                <>
                  {work.findIndex(w => w.page === pageIndex) === 
                   work.findIndex(w => w.page !== undefined) && (
                    <Text style={styles.headingLabel}>Work Experience</Text>
                  )}
                  <WorkPdf works={work.filter(w => w.page === pageIndex)} fontColor={fontColor}/>
                </>
              )}
            </View>
          </View>
        </Page>
      ))}
    </Document>
  );
};
